import { create } from 'zustand';
import { computeFigureHash, saveDraft as saveLocalDraft } from '@/lib/drafts/localDraft';
import { produce } from 'immer';
import type { PlotSpec } from '@shared/schema';
import { apiRequest, authorizedFetch } from '@/lib/queryClient';
import { getPlotId } from '@/lib/plot-id';
import { normalizeInsightsPayload } from '@/lib/insights/insights-schema';

type PlotSpecLike = PlotSpec | { data?: any[]; layout?: Record<string, any> };

export type InsightV2Persistent = {
  schemaVersion: 'InsightV2';
  projectId: string;
  plotId?: string | null;
  versionId?: string | null;
  generatedAt: string;
  model?: string;
  runId?: string | null;
  blocks: Array<{
    id: string;
    role: 'system' | 'assistant' | 'user';
    kind: 'summary' | 'bullet_list' | 'limitation' | 'suggestion' | 'note' | string;
    text: string;
  }>;
};

export interface ClientPlot {
  id: string;
  projectId: string;
  title: string;
  plotlyJson: { data?: any[]; layout?: Record<string, any> };
  pythonCode: string | null;
  javascriptCode?: string | null;
  statsJson?: any;
  insights?: InsightV2Persistent | null;
  plotSpec?: PlotSpecLike;
  createdAt: Date;
  creditsConsumed: number;
  latestVersionId?: string;
  latestDataFingerprintId?: string;
  baselineHash?: string;
  artifactSource?: 'draft' | 'version';
  incompleteLatest?: boolean;
  missingArtifacts?: string[];
  figureCaption?: string | null; // Caption extracted from insights.json
  _apiData?: any;
}

// Track once-per-load logs so we don't spam the console when the same tuple is set repeatedly
const __fingerprintLogOnce = new Set<string>();

// Selection types
export type PlotSelection =
  | { kind: 'none' }
  | { kind: 'point'; traceIndex: number; pointIndex: number }
  | { kind: 'series'; traceIndex: number }
  | { kind: 'axis'; axisId: 'x' | 'y' | 'x2' | 'y2' }
  | { kind: 'legendItem'; traceIndex: number }
  | { kind: 'title' }
  | { kind: 'annotation'; annotationIndex: number }
  | { kind: 'shape'; shapeIndex: number };

export type PlotEditorTabId = 'data-encodings' | 'axes-scales' | 'colors-styles' | 'labels-annotations' | 'legend-guides' | 'layout-subplots';

export type SelectionSource = 'canvas' | 'editor' | 'system';

export interface EditorFocusHint {
  tab?: PlotEditorTabId;
  axesSubtab?: 'x' | 'y';
  expandSectionId?: string;
  scrollToId?: string;
  source?: SelectionSource;
  ts?: number;
}

export interface EditorUI {
  activeTab: PlotEditorTabId;
  followSelection: boolean;
  focusHint: EditorFocusHint | null;
}

interface PlotsState {
  plots: ClientPlot[];
  currentPlot: ClientPlot | null;
  currentByProject?: Record<string, ClientPlot | null>;
  byProjectPlots?: Record<string, Record<string, ClientPlot>>;
  // Separate cache for plot captions (keyed by plotId) - doesn't require plot to be in main store
  plotCaptions?: Record<string, string | null>;
  // Insights cache with timestamps: { data: InsightV2Persistent | null, fetchedAt: number }
  insightsCache?: Record<string, { data: any | null; fetchedAt: number }>;
  editorMode?: 'unbound' | 'bound';
  isGenerating: boolean;
  generationProgress: string;
  editHistory: PlotSpecLike[];
  historyIndex: number;
  error: string | null;
  // Baseline figure storage: keyed by plotId, stores the last saved/loaded baseline figure
  baselineFigureById?: Record<string, any>;
  // Canonical selection state
  selection: PlotSelection;
  editorUI: EditorUI;
  // Legacy fields (kept for migration compatibility)
  selectedPoint: { traceIndex: number; pointIndex: number } | null;
  selectedSeries: number | null;
  hydration?: {
    live: {
      projectId?: string;
      plotId?: string;
      latestDataFingerprintId?: string;
      statsJson?: any;
      pythonCode?: string;
      javascriptCode?: string;
      plotlyJson?: any;
      source?: 'draft' | 'version';
    };
    // Context-keyed mapping of latest fingerprint ids, key: `${projectId}:${plotId}` -> fingerprintId|null
    liveFingerprintByContext?: Record<string, string | null>;
    // Pending fingerprints written before the editor binds to the target context
    pendingFingerprintByContext?: Record<string, string>;
    // Monotonic version to signal watchers of fingerprint updates
    liveFingerprintVersion?: number;
    // Idempotency guard: hydration status per tuple `${projectId}:${plotId}:${fingerprintId}`
    hydratedFlags?: Record<string, boolean>;
    // Hydration guard state for figure snapshots (epoch/dirty/owner)
    guard?: HydrationState;
  };
  
  // Actions
  loadPlots: (projectId: string) => Promise<void>;
  setCurrentPlot: (plot: ClientPlot | null) => void;
  clearCurrentPlot: () => void;
  getCurrentFor: (projectId: string) => ClientPlot | null;
  setCurrentFor: (projectId: string, plot: ClientPlot | null) => void;
  resetAll: () => void;
  resetHydration: () => void;
  setEditorMode: (mode: 'unbound'|'bound') => void;
  updateCurrentPlot: (updater: (draft: ClientPlot) => void, opts?: { pushHistory?: boolean }) => void;
  // generatePlot removed (deprecated)
  updatePlotSpec: (plotId: string, plotSpec: PlotSpec) => Promise<void>;
  savePlotHistory: (plotSpec: PlotSpec) => void;
  undo: () => void;
  redo: () => void;
  deletePlot: (id: string) => Promise<void>;
  setSelectedPoint: (point: { traceIndex: number; pointIndex: number } | null) => void;
  setSelectedSeries: (seriesIndex: number | null) => void;
  
  // New selection actions
  clearSelection: (source?: SelectionSource) => void;
  selectPoint: (traceIndex: number, pointIndex: number, source?: SelectionSource) => void;
  selectSeries: (traceIndex: number, source?: SelectionSource) => void;
  selectAxis: (axisId: 'x' | 'y' | 'x2' | 'y2', source?: SelectionSource) => void;
  selectLegendItem: (traceIndex: number, source?: SelectionSource) => void;
  selectTitle: (source?: SelectionSource) => void;
  selectAnnotation: (annotationIndex: number, source?: SelectionSource) => void;
  selectShape: (shapeIndex: number, source?: SelectionSource) => void;
  
  // Editor UI actions
  setActiveTab: (tab: PlotEditorTabId, source?: SelectionSource) => void;
  setFollowSelection: (on: boolean) => void;
  setFocusHint: (hint: EditorFocusHint | null) => void;
  
  // Atomic update functions
  applyPlotUpdate: (updateFn: (draft: any) => void) => void;
  applyPlotSpec: (updateFn: (draft: any) => void) => void;
  addAnnotation: (annotation: any) => void;
  updateAnnotation: (index: number, patch: any) => void;
  removeAnnotation: (index: number) => void;
  addShape: (shape: any) => void;
  updateShape: (index: number, patch: any) => void;
  removeShape: (index: number) => void;

  // Theme selectors
  selectTheme: () => any;
  selectValueMap: () => { domain: (string|number)[]; colors: Record<string,string> };
  selectColoraxisSettings: () => any;

  // Theme actions
  setMode: (mode: 'categorical'|'sequential'|'diverging') => void;
  setPalette: (swatches: string[]) => void;
  setMappingColor: (value: string|number, color: string) => void;
  lockValue: (value: string|number, locked: boolean) => void;
  setContinuousRange: (min: number|null, max: number|null, mid?: number|null) => void;
  setSharedColoraxis: (enabled: boolean) => void;
  setNaNColor: (hex: string) => void;
  setClamp: (enabled: boolean) => void;
  setMissingCategoryColor: (hex: string) => void;
  setRoles: (patch: Record<string,string>) => void;
  setSeed: (seed: number) => void;

  // Live hydration actions
  getLiveDataFingerprint: (projectId: string, plotId: string) => string | null;
  setLiveDataFingerprint: (projectId: string, plotId: string, id: string | null, meta?: any) => void;
  setCurrentPlotFingerprint: (id: string | null) => void;
  setLiveStats: (stats: any, source?: 'draft' | 'version') => void;
  setLiveCodes: (payload: { pythonCode?: string; javascriptCode?: string; source?: 'draft' | 'version' }) => void;
  applyLiveToCurrentPlot: () => void;
  setCurrentPlotArtifacts: (payload: Partial<ClientPlot> & { id?: string; projectId?: string }, opts?: { bypassGuard?: boolean }) => void;
  clearCurrentPlotArtifacts: (keys?: Array<keyof ClientPlot>) => void;
  // Hydration idempotency helpers
  markFingerprintHydrated: (projectId: string, plotId: string, fingerprintId: string) => void;
  isFingerprintHydrated: (projectId: string, plotId: string, fingerprintId: string) => boolean;

  // Context-keyed helpers
  setPlotByContext: (ctx: { projectId: string; id: string; data: Partial<ClientPlot> }) => void;
  getPlotByContext: (ctx: { projectId: string; id: string }) => ClientPlot | null;
  // Hydrate figure from latest artifacts if embedded snapshot is empty
  hydrateFigureFromArtifacts: (plotId: string) => Promise<void>;
  // Write live figure from WS into hydration.live, preserving source
  setLivePlotlyJson: (fig: any, source?: 'draft' | 'version') => void;

  // Hydration guard API
  maybeApplySnapshot: (snap: IncomingSnapshot, opts?: { force?: boolean }) => 'applied' | 'skipped:user-dirty' | 'skipped:older' | 'skipped:duplicate' | 'skipped:owner-lock';
  markUserEdited: () => void;
  saveDraftLeadingEdge: () => void;
  saveDraftTrailingEdge: () => Promise<void>;
  withSnapshotGuard: (figure: any, meta: { hash?: string | null; updatedAt?: string | null }, source: SnapshotSource, opts?: { force?: boolean }) => 'applied' | 'skipped:user-dirty' | 'skipped:older' | 'skipped:duplicate' | 'skipped:owner-lock';
  markServerSaved: (opts?: { figure?: any; updatedAt?: string | number }) => void;

  // Caption loading for figure composition
  loadInsightsForPlots: (projectId: string, plotIds: string[]) => Promise<void>;
  getPlotCaption: (plotId: string, projectId?: string) => string | null | undefined;

  // Baseline figure management
  setBaselineFigure: (plotId: string, fig: any) => void;
  resetPlotToBaseline: (plotId: string) => { ok: boolean; error?: string };
}

// Normalize a figure and bump editrevision so Plotly re-renders
function normalizeFig(fig: any, prev?: any) {
  const out = structuredClone(fig || {});
  out.layout = out.layout || {};
  const prevRev = Number(prev?.layout?.editrevision || 0);
  out.layout.editrevision = prevRev + 1;
  try { migrateThemeToCanonical(out as any); } catch {}
  return out;
}

// Hydration guard types
export type SnapshotSource = 'baseline' | 'artifact' | 'ws' | 'chat' | 'manual' | 'duplicate' | 'live';

export interface IncomingSnapshot {
  figure: any;
  hash?: string | null;
  updatedAt?: string | null;
  source: SnapshotSource;
}

export interface HydrationState {
  owner: SnapshotSource | null;
  epoch: number;
  dirtyEpoch: number;
  baselineHash?: string | null;
  baselineUpdatedAt?: string | null;
  hydrated: boolean;
  isDirty?: boolean;
  // internals
  _lastLeadingEdgeAt?: number;
  _trailingTimerId?: number | null;
  ownerLockUntilMs?: number;
}

const FEATURE_HYDRATION_GUARD: boolean = (() => {
  try {
    const v = (import.meta as any)?.env?.VITE_FEATURE_HYDRATION_GUARD;
    if (v === '0' || v === 'false') return false;
    if (v === '1' || v === 'true') return true;
  } catch {}
  return true; // default on in dev
})();

const OWNER_LOCK_TTL_MS = 1000; // short lock to let baseline settle
function logHydration(event: 'attempt' | 'applied' | 'skipped', payload: { reason?: string; source: SnapshotSource; epoch: number; dirtyEpoch: number }) {
  try { console.info(`hydration.${event}`, payload); } catch {}
}

// Helper to generate focus hint based on selection and source
function generateFocusHint(selection: PlotSelection, source: SelectionSource, followSelection: boolean): EditorFocusHint | null {
  if (source !== 'canvas' || !followSelection) return null;
  
  const ts = Date.now();
  
  switch (selection.kind) {
    case 'point':
      return {
        tab: 'colors-styles',
        expandSectionId: 'point-controls',
        source,
        ts,
      };
    case 'series':
      return {
        tab: 'colors-styles',
        expandSectionId: 'series-appearance',
        source,
        ts,
      };
    case 'legendItem':
      return {
        tab: 'legend-guides',
        source,
        ts,
      };
    case 'axis':
      const axesSubtab = selection.axisId === 'x' || selection.axisId === 'x2' ? 'x' : 'y';
      return {
        tab: 'axes-scales',
        axesSubtab,
        source,
        ts,
      };
    case 'title':
      return {
        tab: 'labels-annotations',
        expandSectionId: 'titles',
        source,
        ts,
      };
    case 'annotation':
      return {
        tab: 'labels-annotations',
        expandSectionId: 'annotations',
        source,
        ts,
      };
    case 'shape':
      return {
        tab: 'labels-annotations',
        expandSectionId: 'shapes',
        source,
        ts,
      };
    default:
      return null;
  }
}

export const usePlotsStore = create<PlotsState>((set, get) => ({
  plots: [],
  currentPlot: null,
  currentByProject: {},
  byProjectPlots: {},
  plotCaptions: {},
  editorMode: 'unbound',
  isGenerating: false,
  generationProgress: '',
  editHistory: [],
  historyIndex: -1,
  error: null,
  baselineFigureById: {},
  selection: { kind: 'none' },
  editorUI: {
    activeTab: 'data-encodings',
    followSelection: true,
    focusHint: null,
  },
  selectedPoint: null,
  selectedSeries: null,
  hydration: { live: {}, liveFingerprintByContext: {}, pendingFingerprintByContext: {}, liveFingerprintVersion: 0, hydratedFlags: {}, guard: { owner: null, epoch: 0, dirtyEpoch: 0, hydrated: false, isDirty: false } },
  
  loadPlots: async (projectId: string) => {
    try {
      const response = await apiRequest('GET', `/api/projects/${projectId}/plots`);
      const apiPlots = await response.json();
      
      const plots: ClientPlot[] = (apiPlots as any[]).map((apiPlot: any) => {
        const latest = apiPlot.latestVersion || apiPlot.latest_version || null;
        let plotlyJson = latest?.plotlyJson || latest?.plotly_json || { data: [], layout: {} };
        
        // Parse plotlyJson if it's a string (stored as TEXT in database)
        if (typeof plotlyJson === 'string') {
          try {
            plotlyJson = JSON.parse(plotlyJson);
          } catch (e) {
            console.warn('Failed to parse plotlyJson string:', e);
            plotlyJson = { data: [], layout: {} };
          }
        }
        
        // Ensure plotlyJson is an object with data and layout
        if (!plotlyJson || typeof plotlyJson !== 'object') {
          plotlyJson = { data: [], layout: {} };
        }
        if (!Array.isArray(plotlyJson.data)) {
          plotlyJson.data = [];
        }
        if (!plotlyJson.layout || typeof plotlyJson.layout !== 'object') {
          plotlyJson.layout = {};
        }
        if (!plotlyJson.layout.editrevision) {
          plotlyJson.layout.editrevision = 1;
        }
        try { migrateThemeToCanonical(plotlyJson as any); } catch {}

        return {
          id: apiPlot.id,
          projectId: apiPlot.projectId,
          title: apiPlot.title || latest?.label || 'Untitled Plot',
          plotlyJson,
          pythonCode: latest?.code ?? null,
          plotSpec: latest?.plotSpec || latest?.plot_spec,
          createdAt: apiPlot.createdAt ? new Date(apiPlot.createdAt) : new Date(),
          creditsConsumed: latest?.versionNumber ?? 0,
          latestVersionId: latest?.id,
          _apiData: apiPlot,
        };
      });
      
      set({ plots });

      // Only auto-select when entering an existing plot route (`/plot-editor/:projectId/:plotId`)
      try {
        const path = window.location?.pathname || '';
        const parts = path.split('/');
        const routePlotId = parts[3] || '';
        if (routePlotId) {
          const match = plots.find((p) => p.id === routePlotId);
          if (match) {
            // Merge with existing currentPlot to avoid clobbering hydrated snapshot
            const { currentPlot } = get();
            const merged = (currentPlot && currentPlot.id === match.id)
              ? {
                  ...match,
                  // Preserve richer fields already hydrated by editor
                  plotlyJson: (Array.isArray((match as any)?.plotlyJson?.data) && (match as any).plotlyJson.data.length > 0)
                    ? (match as any).plotlyJson
                    : (currentPlot.plotlyJson || (match as any).plotlyJson),
                  pythonCode: match.pythonCode ?? currentPlot.pythonCode ?? null,
                  latestDataFingerprintId: match.latestDataFingerprintId ?? currentPlot.latestDataFingerprintId,
                  baselineHash: match.baselineHash ?? currentPlot.baselineHash,
                  // CRITICAL: Preserve insights from currentPlot if they exist (API plots don't include insights)
                  insights: currentPlot.insights ?? match.insights ?? null,
                }
              : match;
            set({ currentPlot: merged as any });
            // If the embedded figure is empty, fetch latest artifacts to hydrate (fire-and-forget)
            const hasData = Array.isArray((merged as any)?.plotlyJson?.data) && (merged as any).plotlyJson.data.length > 0;
            if (!hasData) {
              try { void get().hydrateFigureFromArtifacts((merged as any).id); } catch {}
            }
            console.log('🎯 Selected plot from route:', merged.title);
          }
          // If no match, do nothing; wait for later fetches to populate
        }
        // If no route plotId, do not preselect - keep canvas empty until user loads/creates data
      } catch {}
      // Don't create demo plots automatically - let user start fresh
      
    } catch (error) {
      console.error('Failed to load plots:', error);
      set({ error: 'Failed to load plots' });
    }
  },
  
  setCurrentPlot: (plot) => {
    if (plot === null) {
      set((state) => ({ currentPlot: null, currentByProject: { ...(state.currentByProject || {}), [String(state.currentPlot?.projectId || '')]: null } }));
      return;
    }
    if (!plot) return;

    const id = getPlotId(plot as any);
    set((state) => {
      const exists = state.plots.some((p) => getPlotId(p as any) === id);
      const cloned = structuredClone(plot);
      try { if ((cloned as any)?.plotlyJson) migrateThemeToCanonical((cloned as any).plotlyJson); } catch {}
      const plots = exists
        ? state.plots.map((p) => (getPlotId(p as any) === id ? cloned : p))
        : [cloned, ...state.plots];

      // Only reset editHistory when loading a DIFFERENT plot
      const isDifferentPlot = !state.currentPlot || getPlotId(state.currentPlot as any) !== id;
      const shouldResetHistory = isDifferentPlot;

      // CRITICAL FIX: Reset hydration guard when switching to a different plot
      // This prevents stale baselineHash/baselineUpdatedAt from previous plot from blocking new plot hydration
      const currentGuard = state.hydration?.guard || { owner: null, epoch: 0, dirtyEpoch: 0, hydrated: false, isDirty: false };
      const shouldResetGuard = isDifferentPlot;
      const nextGuard = shouldResetGuard 
        ? { owner: null, epoch: 0, dirtyEpoch: 0, hydrated: false, isDirty: false }
        : currentGuard;

      const projId = String((cloned as any).projectId || '');

      // CRITICAL: Validate insights.plotId matches the plot being set
      // Clear insights if they belong to a different plot
      if ((cloned as any).insights) {
        const insights = (cloned as any).insights as InsightV2Persistent | null;
        if (insights && insights.plotId && insights.plotId !== id) {
          try { console.warn('[setCurrentPlot] insights plotId mismatch, clearing insights', { insightsPlotId: insights.plotId, targetPlotId: id }); } catch {}
          (cloned as any).insights = null; // Clear mismatched insights
        } else if (insights && insights.schemaVersion === 'InsightV2') {
          // Ensure plotId is set correctly
          insights.plotId = id;
          insights.projectId = projId;
        }
      }
      const by = { ...(state.currentByProject || {}) } as Record<string, ClientPlot | null>;
      if (projId) by[projId] = cloned as any;
 
      // Mirror pending/live fingerprint into current plot on bind
      const k = `${String((cloned as any).projectId || '')}:${String(id)}`;
      const live = state.hydration || {} as any;
      const liveCtx = (live.liveFingerprintByContext || {}) as Record<string, string | null>;
      const pending = (live.pendingFingerprintByContext || {}) as Record<string, string>;
      let nextCurrent: ClientPlot = cloned as any;
      if (!(nextCurrent as any).latestDataFingerprintId) {
        const pend = pending[k];
        const ctx = liveCtx[k] || null;
        if (pend) {
          (nextCurrent as any).latestDataFingerprintId = pend;
          delete pending[k];
        } else if (ctx) {
          (nextCurrent as any).latestDataFingerprintId = ctx as any;
        }
        if (process.env.NODE_ENV !== 'production') {
          try { console.info('liveFID/mirror_on_bind', { projectId: (nextCurrent as any).projectId, plotId: id, fid: (nextCurrent as any).latestDataFingerprintId }); } catch {}
        }
      }

      return {
        currentPlot: nextCurrent,
        currentByProject: by,
        plots,
        // Explicitly preserve baselineFigureById - baselines should persist across plot switches
        baselineFigureById: state.baselineFigureById || {},
        hydration: { 
          ...(state.hydration || {}), 
          pendingFingerprintByContext: pending,
          guard: nextGuard
        },
        editHistory: shouldResetHistory ? [structuredClone((plot as any).plotlyJson)] : state.editHistory,
        historyIndex: shouldResetHistory ? 0 : state.historyIndex,
      } as any;
    });
  },

  clearCurrentPlot: () => {
    set((state) => {
      const projId = String(state.currentPlot?.projectId || '');
      const by = { ...(state.currentByProject || {}) } as Record<string, ClientPlot | null>;
      if (projId) by[projId] = null;
      return { currentPlot: null, currentByProject: by };
    });
  },

  getCurrentFor: (projectId: string) => {
    const by = get().currentByProject || {};
    return (by[String(projectId)] as ClientPlot | null) ?? null;
  },

  setCurrentFor: (projectId: string, plot: ClientPlot | null) => {
    if (!projectId) return;
    set((state) => {
      const by = { ...(state.currentByProject || {}) } as Record<string, ClientPlot | null>;
      by[String(projectId)] = plot ? structuredClone(plot) : null;
      // If the active project matches, mirror to currentPlot for UI continuity
      const active = String(state.currentPlot?.projectId || '');
      const nextCurrent = active && active === String(projectId) ? (plot ? structuredClone(plot) : null) : state.currentPlot;
      return { currentByProject: by, currentPlot: nextCurrent } as any;
    });
  },

  resetAll: () => {
    set({ 
      plots: [], 
      currentPlot: null, 
      currentByProject: {}, 
      byProjectPlots: {}, 
      plotCaptions: {}, 
      editorMode: 'unbound', 
      editHistory: [], 
      historyIndex: -1, 
      error: null, 
      selection: { kind: 'none' },
      editorUI: {
        activeTab: 'data-encodings',
        followSelection: true,
        focusHint: null,
      },
      selectedPoint: null, 
      selectedSeries: null, 
      hydration: { live: {} } 
    });
  },
  resetHydration: () => {
    set((state) => ({ 
      ...state, 
      hydration: { 
        ...(state.hydration || {}),
        live: {},
        guard: { owner: null, epoch: 0, dirtyEpoch: 0, hydrated: false, isDirty: false }
      } 
    } as any));
  },
  setEditorMode: (mode) => set({ editorMode: mode }),
  
  updateCurrentPlot: (updater, opts) => {
    const { pushHistory = false } = opts ?? {};
    const cur = get().currentPlot;
    if (!cur) return;

    const next = structuredClone(cur);
    updater(next);

    set((state) => {
      const id = getPlotId(next as any);
      const plots = state.plots.map((p) => (getPlotId(p as any) === id ? structuredClone(next) : p));

      let editHistory = state.editHistory;
      let historyIndex = state.historyIndex;
      if (pushHistory) {
        editHistory = [...editHistory.slice(0, historyIndex + 1), structuredClone(next.plotlyJson) as PlotSpecLike];
        historyIndex = editHistory.length - 1;
      }

      return {
        currentPlot: next,
        plots,
        editHistory,
        historyIndex,
      };
    });
  },
  
  // generatePlot removed (deprecated)
  
  updatePlotSpec: async (plotId: string, plotSpec: PlotSpec) => {
    try {
      const state = get();
      const existing = state.plots.find((p) => p.id === plotId) || state.currentPlot;
      const payload: Record<string, any> = {
        plotSpec,
        plotlyJson: existing?.plotlyJson ?? { data: [], layout: {} },
        code: existing?.pythonCode ?? undefined,
      };

      const response = await apiRequest('POST', `/api/plots/${plotId}/versions`, payload);
      const version = await response.json();

      set((prev) => ({
        plots: prev.plots.map((p) =>
          p.id === plotId
            ? {
                ...p,
                plotSpec: version.plotSpec || payload.plotSpec,
                plotlyJson: version.plotlyJson || payload.plotlyJson,
                pythonCode: version.code ?? p.pythonCode,
                latestVersionId: version.id ?? p.latestVersionId,
                creditsConsumed: version.versionNumber ?? p.creditsConsumed,
              }
            : p,
        ),
        currentPlot:
          prev.currentPlot?.id === plotId
            ? {
                ...prev.currentPlot,
                plotSpec: version.plotSpec || payload.plotSpec,
                plotlyJson: version.plotlyJson || payload.plotlyJson,
                pythonCode: version.code ?? prev.currentPlot.pythonCode,
                latestVersionId: version.id ?? prev.currentPlot.latestVersionId,
              }
            : prev.currentPlot,
      }));

      get().savePlotHistory(version.plotSpec || plotSpec);
    } catch (error) {
      set({ error: 'Failed to update plot' });
      throw error;
    }
  },
  
  savePlotHistory: (plotSpec: PlotSpec) => {
    set((state) => {
      const newHistory = state.editHistory.slice(0, state.historyIndex + 1);
      newHistory.push(plotSpec);
      
      // Keep only last 10 entries
      if (newHistory.length > 10) {
        newHistory.shift();
      }
      
      return {
        editHistory: newHistory,
        historyIndex: newHistory.length - 1,
      };
    });
  },
  
  undo: () => {
    set((state) => {
      if (state.historyIndex > 0) {
        const newIndex = state.historyIndex - 1;
        const plotSpec = state.editHistory[newIndex];
        
        if (state.currentPlot) {
          // Update current plot with historical spec
          const updatedPlot = { ...state.currentPlot, plotSpec };
          
          return {
            historyIndex: newIndex,
            currentPlot: updatedPlot,
            plots: state.plots.map(p => 
              p.id === state.currentPlot?.id ? updatedPlot : p
            ),
          };
        }
        
        return { historyIndex: newIndex };
      }
      return state;
    });
  },
  
  redo: () => {
    set((state) => {
      if (state.historyIndex < state.editHistory.length - 1) {
        const newIndex = state.historyIndex + 1;
        const plotSpec = state.editHistory[newIndex];
        
        if (state.currentPlot) {
          // Update current plot with future spec
          const updatedPlot = { ...state.currentPlot, plotSpec };
          
          return {
            historyIndex: newIndex,
            currentPlot: updatedPlot,
            plots: state.plots.map(p => 
              p.id === state.currentPlot?.id ? updatedPlot : p
            ),
          };
        }
        
        return { historyIndex: newIndex };
      }
      return state;
    });
  },
  
  deletePlot: async (id: string) => {
    try {
      await apiRequest('DELETE', `/api/plots/${id}`);
      
      set((state) => ({
        plots: state.plots.filter(p => p.id !== id),
        currentPlot: state.currentPlot?.id === id ? null : state.currentPlot,
      }));
    } catch (error) {
      set({ error: 'Failed to delete plot' });
      throw error;
    }
  },
  
  setSelectedPoint: (point: { traceIndex: number; pointIndex: number } | null) => {
    set({ selectedPoint: point });
  },
  
  setSelectedSeries: (seriesIndex) => {
    set({ selectedSeries: seriesIndex });
  },
  
  clearSelection: (source = 'system' as SelectionSource) => {
    set((state) => {
      const nextSelection: PlotSelection = { kind: 'none' };
      const focusHint = state.editorUI.followSelection && source === 'canvas' 
        ? null 
        : state.editorUI.focusHint;
      
      return {
        selection: nextSelection,
        selectedPoint: null,
        selectedSeries: null,
        editorUI: {
          ...state.editorUI,
          focusHint,
        },
      };
    });
  },
  
  selectPoint: (traceIndex: number, pointIndex: number, source = 'system' as SelectionSource) => {
    set((state) => {
      const nextSelection: PlotSelection = { kind: 'point', traceIndex, pointIndex };
      const focusHint = generateFocusHint(nextSelection, source, state.editorUI.followSelection);
      
      return {
        selection: nextSelection,
        selectedPoint: { traceIndex, pointIndex },
        selectedSeries: traceIndex, // Also set legacy selectedSeries for compatibility
        editorUI: {
          ...state.editorUI,
          focusHint,
        },
      };
    });
  },
  
  selectSeries: (traceIndex: number, source = 'system' as SelectionSource) => {
    set((state) => {
      const nextSelection: PlotSelection = { kind: 'series', traceIndex };
      const focusHint = generateFocusHint(nextSelection, source, state.editorUI.followSelection);
      
      return {
        selection: nextSelection,
        selectedSeries: traceIndex,
        selectedPoint: null, // Clear point selection when selecting series
        editorUI: {
          ...state.editorUI,
          focusHint,
        },
      };
    });
  },
  
  selectAxis: (axisId: 'x' | 'y' | 'x2' | 'y2', source = 'system' as SelectionSource) => {
    set((state) => {
      const nextSelection: PlotSelection = { kind: 'axis', axisId };
      const focusHint = generateFocusHint(nextSelection, source, state.editorUI.followSelection);
      
      return {
        selection: nextSelection,
        editorUI: {
          ...state.editorUI,
          focusHint,
        },
      };
    });
  },
  
  selectLegendItem: (traceIndex: number, source = 'system' as SelectionSource) => {
    set((state) => {
      const nextSelection: PlotSelection = { kind: 'legendItem', traceIndex };
      const focusHint = generateFocusHint(nextSelection, source, state.editorUI.followSelection);
      
      return {
        selection: nextSelection,
        selectedSeries: traceIndex, // Also set legacy selectedSeries for compatibility
        selectedPoint: null,
        editorUI: {
          ...state.editorUI,
          focusHint,
        },
      };
    });
  },
  
  selectTitle: (source = 'system' as SelectionSource) => {
    set((state) => {
      const nextSelection: PlotSelection = { kind: 'title' };
      const focusHint = generateFocusHint(nextSelection, source, state.editorUI.followSelection);
      
      return {
        selection: nextSelection,
        editorUI: {
          ...state.editorUI,
          focusHint,
        },
      };
    });
  },
  
  selectAnnotation: (annotationIndex: number, source = 'system' as SelectionSource) => {
    set((state) => {
      const nextSelection: PlotSelection = { kind: 'annotation', annotationIndex };
      const focusHint = generateFocusHint(nextSelection, source, state.editorUI.followSelection);
      
      return {
        selection: nextSelection,
        editorUI: {
          ...state.editorUI,
          focusHint,
        },
      };
    });
  },
  
  selectShape: (shapeIndex: number, source = 'system' as SelectionSource) => {
    set((state) => {
      const nextSelection: PlotSelection = { kind: 'shape', shapeIndex };
      const focusHint = generateFocusHint(nextSelection, source, state.editorUI.followSelection);
      
      return {
        selection: nextSelection,
        editorUI: {
          ...state.editorUI,
          focusHint,
        },
      };
    });
  },
  
  setActiveTab: (tab: PlotEditorTabId, source = 'editor' as SelectionSource) => {
    set((state) => {
      // Allow updates from 'editor' (manual clicks) or 'system' (programmatic/adaptive)
      // Block updates from 'canvas' directly (should go through system)
      // This prevents tab flapping: manual tab clicks should not be overridden immediately by canvas events
      const shouldUpdate = source === 'editor' || source === 'system';
      
      if (!shouldUpdate) {
        console.warn('setActiveTab blocked:', { tab, source, focusHint: state.editorUI.focusHint });
        return state;
      }
      
      return {
        editorUI: {
          ...state.editorUI,
          activeTab: tab,
          // Clear focusHint when manually switching tabs (editor source)
          focusHint: source === 'editor' ? null : state.editorUI.focusHint,
        },
      };
    });
  },
  
  setFollowSelection: (on: boolean) => {
    set((state) => ({
      editorUI: {
        ...state.editorUI,
        followSelection: on,
      },
    }));
  },
  
  setFocusHint: (hint: EditorFocusHint | null) => {
    set((state) => ({
      editorUI: {
        ...state.editorUI,
        focusHint: hint,
      },
    }));
  },
  
  // Atomic update function - single transaction for all changes
  applyPlotUpdate: (updateFn: (draft: any) => void) => {
    // Mark leading edge of a user edit burst
    try { get().markUserEdited(); } catch {}
    set((state) => {
      // Work exclusively with plotlyJson for editor updates
      const currentSpec = state.currentPlot?.plotlyJson;
      if (!currentSpec || !state.currentPlot) return state;
      
      // Use Immer to create immutable update
      const nextSpec = produce(currentSpec, draft => {
        // Apply user's update function
        updateFn(draft);
        
        // Always bump editrevision for Plotly re-render
        if (!draft.layout) draft.layout = {};
        draft.layout.editrevision = ((draft.layout.editrevision as number) || 0) + 1;
      });
      
      // Create updated plot - use plotlyJson as canonical field
      const updatedPlot: ClientPlot = {
        ...state.currentPlot,
        plotlyJson: nextSpec as any
      };
      
      // Update plots array with new object identity
      const id = getPlotId(updatedPlot as any);
      const updatedPlots = state.plots.map(p => 
        getPlotId(p as any) === id ? updatedPlot : p
      );
      
      console.debug('🔄 applyPlotUpdate:', {
        editrevision: nextSpec.layout?.editrevision,
        annotations: nextSpec.layout?.annotations?.length || 0,
        shapes: nextSpec.layout?.shapes?.length || 0
      });
      
      return {
        ...state,
        currentPlot: updatedPlot,
        plots: updatedPlots
      };
    });
  },
  
  // Simplified: Work directly with plotlyJson
  applyPlotSpec: (updateFn: (draft: any) => void) => {
    // Mark leading edge of a user edit burst
    try { get().markUserEdited(); } catch {}
    set((state) => {
      // Get current plotlyJson
      if (!state.currentPlot) return state;
      const prev = state.currentPlot.plotlyJson ?? { data: [], layout: {} };
      
      // Use Immer for immutable update
      const next = produce(prev, (draft) => {
        // Ensure draft has layout object
        if (!draft.layout) draft.layout = {};
        
        // Apply user's update function
        updateFn(draft);
        
        // Always bump editrevision for Plotly re-render
        draft.layout.editrevision = ((draft.layout?.editrevision as number) ?? 0) + 1;
      });
      
      // Create updated plot with new plotlyJson
      const updatedPlot: ClientPlot = {
        ...state.currentPlot,
        plotlyJson: next as any
      };
      
      // Update plots array
      const id = getPlotId(updatedPlot as any);
      const updatedPlots = state.plots.map(p => 
        getPlotId(p as any) === id ? updatedPlot : p
      );
      
      console.log('✅ applyPlotSpec update:', {
        editrevision: next.layout?.editrevision,
        annotations: next.layout?.annotations?.length || 0,
        shapes: next.layout?.shapes?.length || 0,
        hasData: !!next.data,
        dataLength: next.data?.length || 0
      });
      
      return {
        ...state,
        currentPlot: updatedPlot,
        plots: updatedPlots
      };
    });
  },
  
  // Helper functions that use applyPlotSpec
  addAnnotation: (annotation: any) => {
    get().applyPlotSpec(draft => {
      if (!draft.layout) draft.layout = {};
      if (!draft.layout.annotations) draft.layout.annotations = [];
      draft.layout.annotations.push(annotation);
      console.log('🎯 addAnnotation - added:', annotation);
    });
  },
  
  updateAnnotation: (index: number, patch: any) => {
    get().applyPlotSpec(draft => {
      if (!draft.layout?.annotations?.[index]) return;
      Object.assign(draft.layout.annotations[index], patch);
      console.log('🎯 updateAnnotation - index:', index, 'patch:', patch);
    });
  },
  
  removeAnnotation: (index: number) => {
    get().applyPlotSpec(draft => {
      if (!draft.layout?.annotations) return;
      draft.layout.annotations.splice(index, 1);
      console.log('🎯 removeAnnotation - index:', index);
    });
  },
  
  addShape: (shape: any) => {
    get().applyPlotSpec(draft => {
      if (!draft.layout) draft.layout = {};
      if (!draft.layout.shapes) draft.layout.shapes = [];
      draft.layout.shapes.push(shape);
      console.log('🎯 addShape - added:', shape);
    });
  },
  
  updateShape: (index: number, patch: any) => {
    get().applyPlotSpec(draft => {
      if (!draft.layout?.shapes?.[index]) return;
      Object.assign(draft.layout.shapes[index], patch);
      console.log('🎯 updateShape - index:', index, 'patch:', patch);
    });
  },
  
  removeShape: (index: number) => {
    get().applyPlotSpec(draft => {
      if (!draft.layout?.shapes) return;
      draft.layout.shapes.splice(index, 1);
      console.log('🎯 removeShape - index:', index);
    });
  },

  // Theme selectors
  selectTheme: () => {
    const cur = get().currentPlot?.plotlyJson;
    const theme = (cur as any)?.layout?.meta?.lumi?.theme || {};
    return theme;
  },
  selectValueMap: () => {
    const theme = get().selectTheme();
    const domain = Array.isArray(theme?.mappings?.domain) ? theme.mappings.domain : [];
    const colors = theme?.mappings?.colors || {};
    return { domain, colors };
  },
  selectColoraxisSettings: () => {
    const theme = get().selectTheme();
    return {
      coloraxis: theme?.coloraxis || 'coloraxis',
      sequential: theme?.sequential || {},
      diverging: theme?.diverging || {},
      notes: theme?.notes || {}
    };
  },

  // Theme actions
  setMode: (mode) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.mode = mode;
    });
  },
  setPalette: (swatches) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.swatches = Array.isArray(swatches) ? swatches : [];
    });
  },
  setMappingColor: (value, color) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.mappings = lumi.theme.mappings || { by: null, domain: [], colors: {}, locked: [] };
      const key = String(value);
      lumi.theme.mappings.colors = { ...(lumi.theme.mappings.colors || {}), [key]: color };
      if (!Array.isArray(lumi.theme.mappings.domain)) lumi.theme.mappings.domain = [];
      if (!lumi.theme.mappings.domain.map(String).includes(key)) lumi.theme.mappings.domain.push(value as any);
    });
  },
  lockValue: (value, locked) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.mappings = lumi.theme.mappings || { by: null, domain: [], colors: {}, locked: [] };
      const arr: any[] = Array.isArray(lumi.theme.mappings.locked) ? [...lumi.theme.mappings.locked] : [];
      const key = value as any;
      const exists = arr.map(String).includes(String(key));
      const next = locked ? (exists ? arr : [...arr, key]) : arr.filter(v => String(v) !== String(key));
      lumi.theme.mappings.locked = next;
    });
  },
  setContinuousRange: (min, max, mid) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      if ((lumi.theme.mode || 'categorical') === 'diverging') {
        lumi.theme.diverging = lumi.theme.diverging || {};
        lumi.theme.diverging.min = min;
        lumi.theme.diverging.max = max;
        if (mid !== undefined) lumi.theme.diverging.midpoint = mid as any;
      } else {
        lumi.theme.sequential = lumi.theme.sequential || {};
        lumi.theme.sequential.min = min;
        lumi.theme.sequential.max = max;
      }
    });
  },
  setSharedColoraxis: (enabled) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.coloraxis = enabled ? 'coloraxis' : undefined;
    });
  },
  setNaNColor: (hex) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      const id = (lumi.theme.coloraxis || 'coloraxis') as string;
      (draft.layout as any)[id] = { ...(draft.layout as any)[id], nancolor: hex };
    });
  },
  setClamp: (enabled) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.notes = { ...(lumi.theme.notes || {}), clamp: !!enabled };
    });
  },
  setMissingCategoryColor: (hex) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.mappings = lumi.theme.mappings || { by: null, domain: [], colors: {}, locked: [] };
      (lumi.theme.mappings as any).missing = hex;
    });
  },
  setRoles: (patch) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.roles = { ...(lumi.theme.roles || {}), ...(patch || {}) };
    });
  },
  setSeed: (seed) => {
    get().applyPlotSpec(draft => {
      draft.layout = draft.layout || {};
      draft.layout.meta = draft.layout.meta || {};
      (draft.layout.meta as any).lumi = (draft.layout.meta as any).lumi || {};
      const lumi = (draft.layout.meta as any).lumi as any;
      lumi.theme = lumi.theme || {};
      lumi.theme.seed = seed;
    });
  },

  // Live hydration implementations
  getLiveDataFingerprint: (projectId, plotId) => {
    const h = get().hydration;
    const key = `${String(projectId)}:${String(plotId)}`;
    const by = h?.liveFingerprintByContext || {};
    return (by[key] as string | undefined) ?? (h?.live?.latestDataFingerprintId || null) ?? null;
  },
  setLiveDataFingerprint: (projectId, plotId, id, meta) => {
    set((state) => {
      const projId = String(projectId || '');
      const pId = String(plotId || '');
      const key = `${projId}:${pId}`;
      const live = state.hydration?.live || {} as any;
      const by = { ...(state.hydration?.liveFingerprintByContext || {}) } as Record<string, string | null>;
      const pending = { ...(state.hydration?.pendingFingerprintByContext || {}) } as Record<string, string>;
      const version = Number(state.hydration?.liveFingerprintVersion || 0) + 1;
      const flags = { ...(state.hydration?.hydratedFlags || {}) } as Record<string, boolean>;

      // Clear path when id is falsy
      if (!id) {
        if (by[key]) delete by[key];
        // Do not delete flags; they are historical
        const nextHydration = { ...(state.hydration || {}), live: live, liveFingerprintByContext: by, pendingFingerprintByContext: pending, liveFingerprintVersion: version, hydratedFlags: flags } as any;
        const currentMatches = state.currentPlot && String((state.currentPlot as any).projectId || '') === projId && String(getPlotId(state.currentPlot as any)) === pId;
        const updatedCurrent = currentMatches ? ({ ...state.currentPlot, latestDataFingerprintId: undefined } as ClientPlot) : state.currentPlot;
        try { console.info('hydrate:data_fingerprint_cleared', { projectId: projId, plotId: pId, fingerprintId: null, meta }); } catch {}
        try { window.dispatchEvent(new CustomEvent('store:liveDataFingerprintChanged', { detail: { projectId: projId, plotId: pId, fingerprintId: null, meta } })); } catch {}
        return { ...state, hydration: nextHydration, currentPlot: updatedCurrent } as any;
      }

      const prev = (by[key] as any) || live.latestDataFingerprintId || undefined;
      if (prev === id) {
        // Ensure flag entry exists (default false) even on no-op
        const flagKey = `${projId}:${pId}:${id}`;
        if (!(flagKey in flags)) flags[flagKey] = false;
        const nextHydration = { ...(state.hydration || {}), live, liveFingerprintByContext: by, pendingFingerprintByContext: pending, liveFingerprintVersion: version, hydratedFlags: flags } as any;
        return { ...state, hydration: nextHydration } as any;
      }

      by[key] = String(id);
      const flagKey = `${projId}:${pId}:${id}`;
      if (!(flagKey in flags)) flags[flagKey] = false;

      // Mirror to live.latestDataFingerprintId when referring to the active currentPlot context
      const currentMatches = state.currentPlot && String((state.currentPlot as any).projectId || '') === projId && String(getPlotId(state.currentPlot as any)) === pId;
      const nextLive = { ...live, ...(currentMatches ? { latestDataFingerprintId: id } : {}) } as any;

      // Optimistically mirror to currentPlot if context matches
      const updatedCurrent = currentMatches
        ? ({ ...state.currentPlot, latestDataFingerprintId: id } as ClientPlot)
        : state.currentPlot;

      // If not matched, store pending to mirror on bind
      if (!currentMatches && id) pending[key] = String(id);

      const nextHydration = { ...(state.hydration || {}), live: nextLive, liveFingerprintByContext: by, pendingFingerprintByContext: pending, liveFingerprintVersion: version, hydratedFlags: flags } as any;

      // Once-per-load log for first transition from null/undefined to a value for this tuple
      const logKey = `${projId}:${pId}`;
      try {
        if (!prev && id && !__fingerprintLogOnce.has(logKey)) {
          __fingerprintLogOnce.add(logKey);
          if (process.env.NODE_ENV !== 'production') console.info('liveFID/write', { projectId: projId, plotId: pId, fid: id });
        }
      } catch {}

      // Emit store event so listeners can react
      try { window.dispatchEvent(new CustomEvent('store:liveDataFingerprintChanged', { detail: { projectId: projId, plotId: pId, fingerprintId: id, meta } })); } catch {}

      return { ...state, hydration: nextHydration, currentPlot: updatedCurrent } as any;
    });
  },
  setCurrentPlotFingerprint: (id) => {
    set((state) => {
      if (!state.currentPlot) return state as any;
      const next = { ...(state.currentPlot as any), latestDataFingerprintId: id || undefined } as ClientPlot;
      const plots = state.plots.map((p) => (getPlotId(p as any) === getPlotId(next as any) ? next : p));
      if (process.env.NODE_ENV !== 'production') { try { console.info('liveFID/set_current', { plotId: next.id, fid: id || null }); } catch {} }
      return { ...state, currentPlot: next, plots } as any;
    });
  },
  setLiveStats: (stats, source) => {
    if (!stats || (typeof stats === 'object' && Object.keys(stats || {}).length === 0)) return;
    set((state) => {
      const prevJson = state.hydration?.live?.statsJson;
      const isSame = (() => {
        try { return JSON.stringify(prevJson) === JSON.stringify(stats); } catch { return false; }
      })();
      if (isSame) return state;
      const nextHydration = { ...(state.hydration || { live: {} }), live: { ...(state.hydration?.live || {}), statsJson: stats, source: source || state.hydration?.live?.source || 'draft' } } as any;
      // Do not clobber if currentPlot already has richer stats
      const updatedCurrent = state.currentPlot ? { ...state.currentPlot, statsJson: state.currentPlot.statsJson || stats } as ClientPlot : state.currentPlot;
      try { console.info('hydrate:stats_set', { plotId: updatedCurrent?.id, source: nextHydration.live?.source }); } catch {}
      return { ...state, hydration: nextHydration, currentPlot: updatedCurrent } as any;
    });
  },
  setLiveCodes: (payload) => {
    const { pythonCode, javascriptCode, source } = payload || {} as any;
    set((state) => {
      const live = state.hydration?.live || {};
      const nextLive = { ...live } as any;
      if (typeof pythonCode === 'string' && pythonCode.length > 0) nextLive.pythonCode = pythonCode;
      if (typeof javascriptCode === 'string' && javascriptCode.length > 0) nextLive.javascriptCode = javascriptCode;
      if (source) nextLive.source = source;
      const nextHydration = { ...(state.hydration || {}), live: nextLive } as any;
      // Optimistically mirror to currentPlot if empty
      let updatedCurrent = state.currentPlot;
      if (updatedCurrent) {
        const np = (typeof pythonCode === 'string' && pythonCode.length > 0 && (!updatedCurrent.pythonCode || updatedCurrent.pythonCode.length === 0)) ? pythonCode : undefined;
        const nj = (typeof javascriptCode === 'string' && javascriptCode.length > 0 && (!updatedCurrent.javascriptCode || updatedCurrent.javascriptCode.length === 0)) ? javascriptCode : undefined;
        if (np !== undefined || nj !== undefined) {
          updatedCurrent = { ...updatedCurrent, ...(np !== undefined ? { pythonCode: np } : {}), ...(nj !== undefined ? { javascriptCode: nj } : {}) } as ClientPlot;
        }
      }
      try { console.info('hydrate:codes_set', { plotId: updatedCurrent?.id, source: nextLive?.source || 'draft' }); } catch {}
      return { ...state, hydration: nextHydration, currentPlot: updatedCurrent } as any;
    });
  },
  applyLiveToCurrentPlot: () => {
    const state = get();
    if (!state.currentPlot) return;
    const live = state.hydration?.live || {};
    
    // Guard: Only replace currentPlot if the actual plot content changed, not just metadata/timestamps
    if (live?.plotlyJson) {
      const liveHash = computeFigureHash(live.plotlyJson);
      const currentHash = state.currentPlot.baselineHash || computeFigureHash(state.currentPlot.plotlyJson);
      // Compare plot IDs safely - check hydration context if live.plotId is missing
      const plotId = String(getPlotId(state.currentPlot as any));
      const livePlotId = (live as any).plotId || state.hydration?.live?.plotId;
      const sameId = plotId === livePlotId;
      
      // If same plot ID and same figure hash, skip figure replacement to preserve undo history
      if (sameId && liveHash === currentHash) {
        // Still merge non-figure artifacts (stats, code, etc.) but don't replace plotlyJson
        set((s) => {
          const st = s;
          if (!st.currentPlot) return st as any;
          const projId = String((st.currentPlot as any).projectId || '');
          const plotId = String(getPlotId(st.currentPlot as any));
          const by = st.hydration?.liveFingerprintByContext || {};
          const ctxFp = by[`${projId}:${plotId}`];
          const latestFp = live.latestDataFingerprintId || ctxFp;
          const merged: ClientPlot = { ...st.currentPlot } as any;
          if (latestFp) (merged as any).latestDataFingerprintId = latestFp;
          if (live.statsJson && !merged.statsJson) (merged as any).statsJson = live.statsJson as any;
          if (live.pythonCode && !merged.pythonCode) (merged as any).pythonCode = (live.pythonCode as any) ?? null;
          if (live.javascriptCode && !merged.javascriptCode) (merged as any).javascriptCode = (live.javascriptCode as any) ?? null;
          (merged as any).artifactSource = (live.source || merged.artifactSource || undefined) as any;
          try { console.info('hydrate:plot_set(meta_only)', { plotId: (merged as any).id, source: merged.artifactSource || live.source || 'draft', reason: 'same_figure_hash' }); } catch {}
          const plots = st.plots.map((p) => (getPlotId(p as any) === getPlotId(merged as any) ? merged : p));
          return { ...st, currentPlot: merged, plots } as any;
        });
        return; // Early return - no figure replacement needed
      }
      
      // Figure content changed - apply snapshot (this will update baselineHash)
      try { get().maybeApplySnapshot({ figure: live.plotlyJson, source: 'live', hash: liveHash, updatedAt: null }); } catch {}
    }
    
    // Merge non-figure artifacts regardless of figure application
    set((s) => {
      const st = s;
      if (!st.currentPlot) return st as any;
      const projId = String((st.currentPlot as any).projectId || '');
      const plotId = String(getPlotId(st.currentPlot as any));
      const by = st.hydration?.liveFingerprintByContext || {};
      const ctxFp = by[`${projId}:${plotId}`];
      const latestFp = live.latestDataFingerprintId || ctxFp;
      const merged: ClientPlot = { ...st.currentPlot } as any;
      if (latestFp) (merged as any).latestDataFingerprintId = latestFp;
      if (live.statsJson && !merged.statsJson) (merged as any).statsJson = live.statsJson as any;
      if (live.pythonCode && !merged.pythonCode) (merged as any).pythonCode = (live.pythonCode as any) ?? null;
      if (live.javascriptCode && !merged.javascriptCode) (merged as any).javascriptCode = (live.javascriptCode as any) ?? null;
      (merged as any).artifactSource = (live.source || merged.artifactSource || undefined) as any;
      try { console.info('hydrate:plot_set(meta)', { plotId: (merged as any).id, source: merged.artifactSource || live.source || 'draft' }); } catch {}
      const plots = st.plots.map((p) => (getPlotId(p as any) === getPlotId(merged as any) ? merged : p));
      return { ...st, currentPlot: merged, plots } as any;
    });
  },
  setCurrentPlotArtifacts: (payload, opts?: { bypassGuard?: boolean }) => {
    if (!payload) return;
    const state = get();
    const current = state.currentPlot;
    if (!current) return;
    if (payload.id && getPlotId(current as any) !== getPlotId(payload as any)) {
      return;
    }
    const src: SnapshotSource = (payload as any)?.artifactSource === 'version' ? 'artifact' : 'chat';
    if ('plotlyJson' in payload && payload.plotlyJson !== undefined) {
      if (payload.plotlyJson === null) {
        // Explicit clear
        set((s) => {
          if (!s.currentPlot) return s as any;
          const prevRev = Number((s.currentPlot as any).plotlyJson?.layout?.editrevision || 0);
          const cleared = { data: [], layout: { editrevision: prevRev + 1 } } as any;
          const merged: ClientPlot = { ...(s.currentPlot as any), plotlyJson: cleared } as any;
          const plots = s.plots.map((p) => (getPlotId(p as any) === getPlotId(merged as any) ? merged : p));
          return { ...s, currentPlot: merged, plots } as any;
        });
      } else {
        // DIRECT FALLBACK: If bypassGuard is true, set plotlyJson directly without going through guard
        // CRITICAL: Also update guard state so future checks compare against the newly loaded figure
        if (opts?.bypassGuard) {
          try {
            const fig = payload.plotlyJson as any;
            const normalized = normalizeFig(fig, current.plotlyJson);
            const figHash = computeFigureHash(normalized);
            const nowIso = String(Date.now());
            
            set((s) => {
              if (!s.currentPlot) return s as any;
              const merged: ClientPlot = { ...s.currentPlot, plotlyJson: normalized } as any;
              const plots = s.plots.map((p) => (getPlotId(p as any) === getPlotId(merged as any) ? merged : p));
              
              // Update guard state to reflect the bypassed figure
              const currentGuard = s.hydration?.guard || { owner: null, epoch: 0, dirtyEpoch: 0, hydrated: false, isDirty: false };
              const nextGuard: HydrationState = {
                ...currentGuard,
                owner: src, // Mark as owned by the source that provided the figure
                hydrated: true,
                baselineHash: figHash,
                baselineUpdatedAt: nowIso,
                epoch: (currentGuard.epoch || 0) + 1,
                // Don't clear isDirty here - preserve user edits if any
              };
              
              try { console.info('setCurrentPlotArtifacts: bypassed guard, set plotlyJson directly and updated guard', { 
                plotId: getPlotId(merged as any), 
                dataLength: normalized?.data?.length,
                baselineHash: figHash,
                owner: src
              }); } catch {}
              
              return { 
                ...s, 
                currentPlot: merged, 
                plots,
                hydration: {
                  ...(s.hydration || {}),
                  guard: nextGuard
                }
              } as any;
            });
          } catch (err) {
            try { console.error('setCurrentPlotArtifacts: bypassGuard failed', err); } catch {}
          }
        } else {
          try {
            get().maybeApplySnapshot({ figure: payload.plotlyJson as any, source: src, hash: computeFigureHash(payload.plotlyJson as any), updatedAt: null }, { force: false });
          } catch {}
        }
      }
    }
    // Apply non-figure fields
    set((s) => {
      if (!s.currentPlot) return s as any;
      const merged: ClientPlot = { ...s.currentPlot } as any;
      if ('statsJson' in payload && payload.statsJson !== undefined) (merged as any).statsJson = payload.statsJson as any;
      if ('insights' in payload && payload.insights !== undefined) {
        // CRITICAL: Validate insights.plotId matches the plot being updated
        const insights = payload.insights as InsightV2Persistent | null;
        const targetPlotId = payload.id || merged.id;
        if (insights && insights.plotId && insights.plotId !== targetPlotId) {
          try { console.warn('[setCurrentPlotArtifacts] insights plotId mismatch, clearing insights', { insightsPlotId: insights.plotId, targetPlotId }); } catch {}
          (merged as any).insights = null; // Clear mismatched insights
        } else {
          // Ensure plotId is set correctly
          if (insights && insights.schemaVersion === 'InsightV2') {
            insights.plotId = targetPlotId;
            insights.projectId = payload.projectId || merged.projectId;
          }
          (merged as any).insights = insights as any;
        }
      }
      if ('pythonCode' in payload && payload.pythonCode !== undefined) (merged as any).pythonCode = payload.pythonCode as any;
      if ('javascriptCode' in payload && payload.javascriptCode !== undefined) (merged as any).javascriptCode = payload.javascriptCode as any;
      if ('latestDataFingerprintId' in payload && payload.latestDataFingerprintId !== undefined) (merged as any).latestDataFingerprintId = payload.latestDataFingerprintId as any;
      if ('latestVersionId' in payload && payload.latestVersionId !== undefined) (merged as any).latestVersionId = payload.latestVersionId as any;
      if ('projectId' in payload && payload.projectId !== undefined) (merged as any).projectId = payload.projectId as any;
      if ('artifactSource' in payload && payload.artifactSource !== undefined) (merged as any).artifactSource = payload.artifactSource as any;
      const plots = s.plots.map((p) => (getPlotId(p as any) === getPlotId(merged as any) ? merged : p));
      const by = { ...(s.currentByProject || {}) } as Record<string, ClientPlot | null>;
      const projId = String((merged as any).projectId || '');
      if (projId) by[projId] = merged;
      const cache = { ...(s.byProjectPlots || {}) } as Record<string, Record<string, ClientPlot>>;
      if (projId) {
        cache[projId] = cache[projId] || {};
        cache[projId][getPlotId(merged as any)] = merged;
      }
      return { ...s, currentPlot: merged, plots, byProjectPlots: cache } as any;
    });
  },

  clearCurrentPlotArtifacts: (keys) => {
    set((state) => {
      if (!state.currentPlot) return state;
      const target: ClientPlot = { ...state.currentPlot } as any;
      const toClear = (keys && keys.length ? keys : ['latestDataFingerprintId', 'pythonCode', 'javascriptCode', 'statsJson'] as any) as Array<keyof ClientPlot>;
      for (const k of toClear) {
        (target as any)[k] = undefined;
      }
      const plots = state.plots.map((p) => (getPlotId(p as any) === getPlotId(target as any) ? target : p));
      return { ...state, currentPlot: target, plots } as any;
    });
  },

  markFingerprintHydrated: (projectId, plotId, fingerprintId) => {
    set((state) => {
      const flags = { ...(state.hydration?.hydratedFlags || {}) } as Record<string, boolean>;
      const key = `${String(projectId)}:${String(plotId)}:${String(fingerprintId)}`;
      flags[key] = true;
      const nextHydration = { ...(state.hydration || {}), hydratedFlags: flags } as any;
      return { ...state, hydration: nextHydration } as any;
    });
  },
  isFingerprintHydrated: (projectId, plotId, fingerprintId) => {
    const flags = (get().hydration?.hydratedFlags || {}) as Record<string, boolean>;
    const key = `${String(projectId)}:${String(plotId)}:${String(fingerprintId)}`;
    return !!flags[key];
  },

  setPlotByContext: ({ projectId, id, data }) => {
    if (!projectId || !id) return;
    set((state) => {
      const cache = { ...(state.byProjectPlots || {}) } as Record<string, Record<string, ClientPlot>>;
      cache[projectId] = cache[projectId] || {};
      const prev = cache[projectId][id] || ({} as ClientPlot);
      const next: ClientPlot = { ...prev, id, projectId, title: prev.title || 'Generated Plot', plotlyJson: prev.plotlyJson || { data: [], layout: {} }, pythonCode: prev.pythonCode || null, creditsConsumed: prev.creditsConsumed || 0, createdAt: prev.createdAt || new Date(), ...(data as any) } as any;
      cache[projectId][id] = next;

      // Only mirror to visible state when context matches active project
      const activeProjectId = String(state.currentPlot?.projectId || '');
      const mirrorVisible = (activeProjectId && activeProjectId === String(projectId));
      const plots = mirrorVisible
        ? (function() {
            const exists = state.plots.some((p) => getPlotId(p as any) === id);
            return exists ? state.plots.map((p) => (getPlotId(p as any) === id ? next : p)) : [next, ...state.plots];
          })()
        : state.plots;
      const currentPlot = mirrorVisible && state.currentPlot && getPlotId(state.currentPlot as any) === id ? next : state.currentPlot;
      const currentByProject = { ...(state.currentByProject || {}) };
      currentByProject[projectId] = mirrorVisible ? (currentPlot as any) : (currentByProject[projectId] || null);
      return { ...state, byProjectPlots: cache, plots, currentPlot, currentByProject } as any;
    });
  },

  getPlotByContext: ({ projectId, id }) => {
    const cache = (get().byProjectPlots || {}) as Record<string, Record<string, ClientPlot>>;
    return (cache[projectId] && cache[projectId][id]) ? cache[projectId][id] : null;
  },

  hydrateFigureFromArtifacts: async (plotId: string) => {
    const cur = get().currentPlot;
    if (!cur || getPlotId(cur as any) !== plotId) return;

    async function fetchJson(url: string) {
      const res = await apiRequest('GET', url);
      if (!res.ok) throw new Error(`fetch failed: ${url} -> ${res.status}`);
      return await res.json();
    }

    function normalizeFig(fig: any, prev?: any) {
      const out = structuredClone(fig || {});
      out.layout = out.layout || {};
      const prevRev = Number(prev?.layout?.editrevision || 0);
      out.layout.editrevision = prevRev + 1;
      try { migrateThemeToCanonical(out as any); } catch {}
      return out;
    }

    try {
      let fig: any | null = null;
      try {
        fig = await fetchJson(`/api/ai/v2/plots/${plotId}/versions/latest/plotly_figure.json`);
      } catch {
        try { fig = await fetchJson(`/api/ai/v2/plots/${plotId}/versions/latest/figure.json`); } catch {}
      }
      if (fig) {
        const next = normalizeFig(fig, cur.plotlyJson);
        const figHash = computeFigureHash(next);
        
        // Check if we need to force apply or bypass guard
        const currentPlotlyJson = cur.plotlyJson;
        const hasEmptyData = !currentPlotlyJson || !Array.isArray(currentPlotlyJson?.data) || currentPlotlyJson.data.length === 0;
        const guard = get().hydration?.guard;
        const currentBaselineHash = guard?.baselineHash || null;
        const hashMismatch = currentBaselineHash && figHash !== currentBaselineHash;
        const shouldForce = hasEmptyData || hashMismatch || !guard?.hydrated;
        
        // Try guard first with force if needed
        const guardResult = get().maybeApplySnapshot({ 
          figure: next, 
          source: 'artifact', 
          hash: figHash, 
          updatedAt: null 
        }, { force: shouldForce });
        
        const wasApplied = guardResult === 'applied';
        
        // If guard skipped and we have valid data, use bypass fallback
        const needsBypass = !wasApplied && Array.isArray(fig.data) && fig.data.length > 0;
        if (needsBypass) {
          try {
            get().setCurrentPlotArtifacts({ 
              id: plotId, 
              plotlyJson: next,
              artifactSource: 'version' // CRITICAL: Set artifactSource so guard owner is 'artifact', not 'chat'
            } as any, { 
              bypassGuard: true 
            });
            try { console.info('hydrateFigureFromArtifacts: guard skipped, used bypass fallback', { plotId, guardResult, dataLength: fig.data.length }); } catch {}
          } catch (bypassErr) {
            try { console.error('hydrateFigureFromArtifacts: bypass fallback failed', bypassErr); } catch {}
          }
        }
        
        try { window.dispatchEvent(new CustomEvent('store:plotlyFigureHydrated', { detail: { plotId } })); } catch {}
      }
      // Probe required artifacts for completeness signalling
      const missing: string[] = [];
      let statsOk = true;
      let scriptOk = true;
      try {
        const r = await apiRequest('GET', `/api/ai/v2/plots/${plotId}/versions/latest/stats.json`);
        if (!r.ok) { statsOk = false; }
      } catch { statsOk = false; }
      try {
        const ed = await apiRequest('GET', `/api/ai/v2/plots/${plotId}/versions/latest/edited_script.py`);
        if (!ed.ok) {
          const sc = await apiRequest('GET', `/api/ai/v2/plots/${plotId}/versions/latest/script.py`);
          if (!sc.ok) scriptOk = false;
        }
      } catch { scriptOk = false; }
      if (!statsOk) missing.push('stats.json');
      if (!scriptOk) missing.push('edited_script.py');
      if (missing.length > 0) {
        get().setCurrentPlotArtifacts({ id: cur.id, incompleteLatest: true as any, missingArtifacts: missing as any } as any);
      } else {
        get().setCurrentPlotArtifacts({ id: cur.id, incompleteLatest: false as any, missingArtifacts: [] as any } as any);
      }
    } catch (e) {
      console.warn('hydrateFigureFromArtifacts failed', (e as any)?.message);
    }
  },

  setLivePlotlyJson: (fig, source) => {
    if (!fig) return;
    set((state) => {
      const live = state.hydration?.live || {};
      const next = { ...live, plotlyJson: fig, ...(source ? { source } : {}) } as any;
      return { ...state, hydration: { ...(state.hydration || {}), live: next } } as any;
    });
  },

  // Hydration guard implementation
  maybeApplySnapshot: (snap, opts) => {
    const s = get();
    if (!s.currentPlot) return 'skipped:owner-lock';
    if (!FEATURE_HYDRATION_GUARD) {
      // Fallback: apply naively
      const prev = s.currentPlot.plotlyJson;
      const nextFig = normalizeFig(snap.figure, prev);
      let outcome: 'applied' | `skipped:${string}` = 'applied';
      set((st) => {
        if (!st.currentPlot) return st as any;
        const merged: ClientPlot = { ...st.currentPlot, plotlyJson: nextFig } as any;
        return { ...st, currentPlot: merged } as any;
      });
      return outcome;
    }
    let outcome: 'applied' | `skipped:${string}` = 'applied';
    set((st) => {
      const guard = { ...(st.hydration?.guard || { owner: null, epoch: 0, dirtyEpoch: 0, hydrated: false, isDirty: false }) } as HydrationState;
      const prevEpoch = guard.epoch || 0;
      const dirtyEpoch = guard.dirtyEpoch || 0;
      const baselineHash = guard.baselineHash || null;
      const baselineUpdatedAt = guard.baselineUpdatedAt || null;
      const snapHash = snap.hash || null;
      const source = snap.source;

      logHydration('attempt', { source, epoch: prevEpoch, dirtyEpoch });

      // 1) User dirty guard
      if (!opts?.force && dirtyEpoch > prevEpoch) {
        logHydration('skipped', { reason: 'user-dirty', source, epoch: prevEpoch, dirtyEpoch });
        outcome = 'skipped:user-dirty';
        return { ...st } as any;
      }
      // 2) Duplicate hash (skip unless forced)
      if (!opts?.force && snapHash && baselineHash && snapHash === baselineHash) {
        logHydration('skipped', { reason: 'duplicate', source, epoch: prevEpoch, dirtyEpoch });
        outcome = 'skipped:duplicate';
        return { ...st } as any;
      }
      // 3) Older snapshot guard (skip unless forced)
      const snapUpdated = snap.updatedAt ? Number(snap.updatedAt) : NaN;
      const baseUpdated = baselineUpdatedAt ? Number(baselineUpdatedAt) : NaN;
      if (!opts?.force && !Number.isNaN(snapUpdated) && !Number.isNaN(baseUpdated) && snapUpdated <= baseUpdated) {
        logHydration('skipped', { reason: 'older', source, epoch: prevEpoch, dirtyEpoch });
        outcome = 'skipped:older';
        return { ...st } as any;
      }
      // 4) First hydration wins
      if (!guard.hydrated) {
        // Apply
        if (!st.currentPlot) return st as any;
        const prevFig = st.currentPlot.plotlyJson;
        const nextFig = normalizeFig(snap.figure, prevFig);
        const merged: ClientPlot = { ...st.currentPlot, plotlyJson: nextFig } as any;
        const plots = st.plots.map((p) => (getPlotId(p as any) === getPlotId(merged as any) ? merged : p));
        const nextEpoch = prevEpoch + 1;
        const shouldClearDirty = (dirtyEpoch === prevEpoch);
        const nextGuard: HydrationState = {
          ...guard,
          owner: source,
          hydrated: true,
          epoch: nextEpoch,
          baselineHash: snapHash ?? computeFigureHash(nextFig),
          baselineUpdatedAt: snap.updatedAt ?? String(Date.now()),
          isDirty: shouldClearDirty ? false : guard.isDirty,
          ownerLockUntilMs: Date.now() + OWNER_LOCK_TTL_MS,
        };
        logHydration('applied', { source, epoch: nextEpoch, dirtyEpoch });
        outcome = 'applied';
        return { ...st, currentPlot: merged, isDirty: !!nextGuard.isDirty, hydration: { ...(st.hydration || {}), guard: nextGuard } } as any;
      }
      // 5) Owner lock (strict during TTL unless forced)
      if (guard.hydrated && guard.owner && guard.owner !== source && !opts?.force) {
        const lockActive = typeof guard.ownerLockUntilMs === 'number' && Date.now() <= (guard.ownerLockUntilMs as number);
        if (lockActive) {
          logHydration('skipped', { reason: 'owner-lock-ttl', source, epoch: prevEpoch, dirtyEpoch });
          outcome = 'skipped:owner-lock';
          return { ...st } as any;
        }
      }
      // 6) Apply subsequent snapshot; never clear isDirty for non-baseline sources
      if (!st.currentPlot) return st as any;
      const prevFig = st.currentPlot.plotlyJson;
      const nextFig = normalizeFig(snap.figure, prevFig);
      const merged: ClientPlot = { ...st.currentPlot, plotlyJson: nextFig } as any;
      const nextEpoch = prevEpoch + 1;
      const nextGuard: HydrationState = {
        ...guard,
        epoch: nextEpoch,
        // Update baseline tracking for sources that represent durable baselines; keep for others
        baselineHash: guard.baselineHash ?? (snapHash || computeFigureHash(nextFig)),
        baselineUpdatedAt: guard.baselineUpdatedAt ?? (snap.updatedAt ?? String(Date.now())),
        ownerLockUntilMs: undefined,
        // Never clear isDirty here
      };
      logHydration('applied', { source, epoch: nextEpoch, dirtyEpoch });
      outcome = 'applied';
      return { ...st, currentPlot: merged, hydration: { ...(st.hydration || {}), guard: nextGuard } } as any;
    });
    return outcome;
  },

  markUserEdited: () => {
    if (!FEATURE_HYDRATION_GUARD) return;
    // Guard: skip if reset is in progress (prevents re-marking dirty during reset)
    if ((window as any).__lumiplotIsResetting) return;
    set((st) => {
      const guard = { ...(st.hydration?.guard || { owner: null, epoch: 0, dirtyEpoch: 0, hydrated: false, isDirty: false }) } as HydrationState;
      const wasDirty = !!guard.isDirty;
      const next: HydrationState = { ...guard } as any;
      if (!wasDirty) {
        // First edit in burst: bump dirtyEpoch
        next.dirtyEpoch = (next.dirtyEpoch || 0) + 1;
        next.isDirty = true;
        // Leading-edge draft write
        (window as any).__scheduleLeadingEdge = true;
      }
      // Always (re)schedule trailing save debounce
      try { if (next._trailingTimerId) clearTimeout(next._trailingTimerId as any); } catch {}
      const scheduledEpoch = next.dirtyEpoch;
      const timer = window.setTimeout(async () => {
        // epoch-aware trailing save
        const cur = usePlotsStore.getState();
        const g = cur.hydration?.guard || ({} as any);
        if ((g as any).dirtyEpoch === scheduledEpoch) {
          try { await cur.saveDraftTrailingEdge(); } catch {}
        }
      }, 2000);
      next._trailingTimerId = timer as any;
      const out = { ...st, hydration: { ...(st.hydration || {}), guard: next } } as any;
      return out;
    });
    // Leading-edge save after state set (throttled ≥500ms)
    try { get().saveDraftLeadingEdge(); } catch {}
  },

  saveDraftLeadingEdge: () => {
    if (!FEATURE_HYDRATION_GUARD) return;
    const st = get();
    const guard = (st.hydration?.guard || {}) as HydrationState;
    const now = Date.now();
    const last = guard._lastLeadingEdgeAt ? Number(guard._lastLeadingEdgeAt) : 0;
    if (last > 0 && now - last < 500) return;
    // Record timestamp immediately to throttle
    set((s) => ({ ...s, hydration: { ...(s.hydration || {}), guard: { ...(s.hydration?.guard || {}), _lastLeadingEdgeAt: now } as any } } as any));
    // Persist in an idle callback where possible
    const schedule = (fn: () => void) => {
      try { (window as any).requestIdleCallback ? (window as any).requestIdleCallback(fn) : setTimeout(fn, 0); } catch { setTimeout(fn, 0); }
    };
    schedule(() => {
      try {
        const state = usePlotsStore.getState();
        const cp = state.currentPlot as any;
        if (!cp) return;
        const pid = String(cp.projectId || state.currentPlot?.projectId || '');
        const plid = String(getPlotId(cp));
        const baselineHash = (state.hydration?.guard?.baselineHash as any) ?? 'EMPTY';
        const baselineFigure = null;
        const workingFigure = (cp as any).plotlyJson;
        saveLocalDraft({ projectId: pid || undefined, plotId: plid, baselineHash: baselineHash as any, baselineFigure, workingFigure, promoteLastGood: true });
      } catch {}
    });
  },

  saveDraftTrailingEdge: async () => {
    if (!FEATURE_HYDRATION_GUARD) return;
    const st = get();
    const cp = st.currentPlot as any;
    if (!cp) return;
    const pid = String(cp.projectId || st.currentPlot?.projectId || '');
    const plid = String(getPlotId(cp));
    const baselineHash = (st.hydration?.guard?.baselineHash as any) ?? 'EMPTY';
    const baselineFigure = null;
    const workingFigure = (cp as any).plotlyJson;
    try { saveLocalDraft({ projectId: pid || undefined, plotId: plid, baselineHash: baselineHash as any, baselineFigure, workingFigure, promoteLastGood: true }); } catch {}
    // Do not clear isDirty on autosave; only markServerSaved clears it
  },

  withSnapshotGuard: (figure, meta, source, opts) => {
    const hash = meta?.hash ?? (figure ? computeFigureHash(figure) : null);
    const updatedAt = meta?.updatedAt ?? null;
    return get().maybeApplySnapshot({ figure, hash, updatedAt, source }, opts);
  },
  markServerSaved: (opts) => {
    const figure = opts?.figure;
    const updatedAt = opts?.updatedAt;
    set((s) => {
      const g = { ...(s.hydration?.guard || {}) } as HydrationState;
      const cur = s.currentPlot as any;
      const fig = figure || (cur ? (cur as any).plotlyJson : null);
      if (fig) {
        g.baselineHash = computeFigureHash(fig);
      }
      if (updatedAt !== undefined && updatedAt !== null) {
        g.baselineUpdatedAt = String(updatedAt);
      } else {
        g.baselineUpdatedAt = String(Date.now());
      }
      g.isDirty = false;
      return { ...s, hydration: { ...(s.hydration || {}), guard: g } } as any;
    });
  },

  // Load insights.json for multiple plots and extract figure_caption
  loadInsightsForPlots: async (projectId: string, plotIds: string[]) => {
    if (!plotIds || plotIds.length === 0) return;

    const state = get();
    // Dedupe plot IDs
    const uniquePlotIds = Array.from(new Set(plotIds));
    
    // Check caption cache first - don't require plot to be in store
    const captionCache = state.plotCaptions || {};
    const now = Date.now();
    const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL for null results
    
    // Track insights cache with timestamps: { data: Insight | null, fetchedAt: number }
    const insightsCache = state.insightsCache || {};
    
    // First, update caption cache from fresh insights cache entries
    const captionCacheUpdates: Record<string, string | null> = {};
    uniquePlotIds.forEach(plotId => {
      const cachedInsight = insightsCache[plotId];
      if (cachedInsight) {
        const age = now - cachedInsight.fetchedAt;
        if (age < CACHE_TTL_MS && cachedInsight.data !== undefined) {
          const caption = cachedInsight.data?.figure_caption || null;
          if (caption !== null || !(plotId in captionCache)) {
            captionCacheUpdates[plotId] = caption;
          }
        }
      }
    });
    
    // Apply caption cache updates in one batch
    if (Object.keys(captionCacheUpdates).length > 0) {
      set((s) => ({
        plotCaptions: { ...(s.plotCaptions || {}), ...captionCacheUpdates }
      }));
    }
    
    const plotsToFetch = uniquePlotIds.filter(plotId => {
      // Check if we have a cached insight value (including null) that's fresh enough
      const cachedInsight = insightsCache[plotId];
      if (cachedInsight) {
        const age = now - cachedInsight.fetchedAt;
        // If cached within TTL, skip fetch
        if (age < CACHE_TTL_MS) {
          return false; // Skip fetch - cached and fresh
        }
      }
      
      // Also check legacy caption cache for backward compatibility
      const cached = captionCache[plotId];
      if (cached !== undefined && cached !== null) {
        return false; // Already have a caption
      }
      
      return true; // Need to fetch
    });

    if (plotsToFetch.length === 0) {
      console.log('[loadInsightsForPlots] All insights already cached (fresh)');
      return;
    }

    console.log('[loadInsightsForPlots] Fetching insights for plots:', plotsToFetch);

    // Fetch insights for all plots in parallel
    const fetchPromises = plotsToFetch.map(async (plotId) => {
      try {
        const response = await authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/insights.json`);
        if (!response.ok) {
          console.log(`[loadInsightsForPlots] Plot ${plotId}: insights.json not found (${response.status})`);
          // Cache null result with timestamp
          return { plotId, insight: null, fetchedAt: now };
        }
        const rawInsights = await response.json();
        const normalized = normalizeInsightsPayload(rawInsights);
        const caption = String(normalized.figure_caption || '').trim() || null;
        console.log(`[loadInsightsForPlots] Plot ${plotId}: normalized caption:`, caption ? `"${caption.substring(0, 50)}..."` : 'null');
        return { plotId, insight: normalized, fetchedAt: now };
      } catch (error) {
        console.warn(`[loadInsightsForPlots] Failed to load insights for plot ${plotId}:`, error);
        // Cache null result with timestamp
        return { plotId, insight: null, fetchedAt: now };
      }
    });

    const results = await Promise.all(fetchPromises);

    console.log('[loadInsightsForPlots] Results:', results.map(r => ({ plotId: r.plotId, hasInsight: !!r.insight })));

    // Update plots store with insights and captions
    set((state) => {
      // Update insights cache with timestamps
      const updatedInsightsCache = { ...(state.insightsCache || {}) };
      const updatedCaptionCache = { ...(state.plotCaptions || {}) };
      
      results.forEach(({ plotId, insight, fetchedAt }) => {
        // Store insight with timestamp (including null results)
        updatedInsightsCache[plotId] = { data: insight, fetchedAt };
        
        // Extract caption from insight
        const caption = insight?.figure_caption ? String(insight.figure_caption).trim() || null : null;
        // Update caption cache (null results are cached too, but with timestamp check above)
        if (caption !== null || !(plotId in updatedCaptionCache)) {
          updatedCaptionCache[plotId] = caption;
        }
      });
      
      // Update plots array if plots exist
      const updatedPlots = state.plots.map((plot) => {
        const result = results.find(r => r.plotId === plot.id);
        if (result && result.insight) {
          const caption = result.insight?.figure_caption ? String(result.insight.figure_caption).trim() || null : null;
          if (caption !== null) {
            return { ...plot, figureCaption: caption };
          }
        }
        return plot;
      });

      // Update currentPlot if it matches
      let updatedCurrentPlot = state.currentPlot;
      const currentResult = results.find(r => r.plotId === state.currentPlot?.id);
      if (currentResult && currentResult.insight && updatedCurrentPlot) {
        const caption = currentResult.insight?.figure_caption ? String(currentResult.insight.figure_caption).trim() || null : null;
        if (caption !== null) {
          updatedCurrentPlot = { ...updatedCurrentPlot, figureCaption: caption };
        }
      }

      // Update byProjectPlots cache
      const updatedByProjectPlots = { ...(state.byProjectPlots || {}) };
      if (projectId && updatedByProjectPlots[projectId]) {
        updatedByProjectPlots[projectId] = { ...updatedByProjectPlots[projectId] };
        results.forEach((result) => {
          if (result.insight) {
            const caption = result.insight?.figure_caption ? String(result.insight.figure_caption).trim() || null : null;
            if (caption !== null && updatedByProjectPlots[projectId][result.plotId]) {
              updatedByProjectPlots[projectId][result.plotId] = {
                ...updatedByProjectPlots[projectId][result.plotId],
                figureCaption: caption,
              };
            }
          }
        });
      }

      return {
        ...state,
        plotCaptions: updatedCaptionCache,
        insightsCache: updatedInsightsCache,
        plots: updatedPlots,
        currentPlot: updatedCurrentPlot,
        byProjectPlots: updatedByProjectPlots,
      } as any;
    });
  },

  // Get caption for a plot (from cache or null if not loaded)
  getPlotCaption: (plotId: string, projectId?: string) => {
    const state = get();
    // First check dedicated caption cache (fastest, doesn't require plot in store)
    const captionCache = state.plotCaptions || {};
    if (plotId in captionCache) {
      // Return the caption (could be string or null - null means we checked and it doesn't exist)
      const cached = captionCache[plotId];
      console.log(`[getPlotCaption] Plot ${plotId}: found in cache:`, cached !== null ? `"${String(cached).substring(0, 50)}..."` : 'null');
      return cached !== null ? cached : undefined;
    }
    console.log(`[getPlotCaption] Plot ${plotId}: not in cache, checking plot objects`);
    // Fallback to checking plot objects
    const plot = state.plots.find(p => p.id === plotId) ||
                 (state.currentPlot?.id === plotId ? state.currentPlot : null) ||
                 (projectId ? state.getPlotByContext?.({ projectId, id: plotId }) : null);
    const result = plot?.figureCaption;
    // If no caption found, try extracting title from plotlyJson layout
    if (!result && plot?.plotlyJson) {
      try {
        const titleFromFig = plot.plotlyJson?.layout?.title?.text || plot.plotlyJson?.layout?.title;
        if (typeof titleFromFig === 'string' && titleFromFig.trim() && titleFromFig !== 'Draft (in progress)') {
          console.log(`[getPlotCaption] Plot ${plotId}: extracted title from plotlyJson:`, `"${titleFromFig.substring(0, 50)}..."`);
          return titleFromFig.trim();
        }
      } catch {}
    }
    console.log(`[getPlotCaption] Plot ${plotId}: from plot object:`, result ? `"${String(result).substring(0, 50)}..."` : 'undefined');
    return result;
  },

  // Set baseline figure for a plot (called on load and after successful save)
  // CRITICAL: This baseline must persist across resets - it's the "last saved" state
  setBaselineFigure: (plotId: string, fig: any) => {
    if (!plotId || !fig) {
      console.warn('[setBaselineFigure] Invalid args:', { plotId, hasFig: !!fig });
      return;
    }
    console.log('[setBaselineFigure] Storing baseline figure:', { plotId, hasFigure: !!fig, dataLength: fig?.data?.length });
    set((state) => {
      const currentBaselines = state.baselineFigureById || {};
      // Always update baseline when explicitly set (on load/save) - this is the source of truth
      const updated = {
        ...currentBaselines,
        [plotId]: structuredClone(fig),
      };
      console.log('[setBaselineFigure] Updated baselineFigureById:', { plotId, allKeys: Object.keys(updated) });
      return {
        baselineFigureById: updated,
      };
    });
  },

  // Reset plot to baseline: restore baseline figure, clear dirty flags, reset histories
  resetPlotToBaseline: (plotId: string) => {
    const state = get();
    const baselineFigure = state.baselineFigureById?.[plotId];
    
    if (!baselineFigure) {
      return { ok: false, error: 'No baseline figure available for this plot' };
    }

    const current = state.currentPlot;
    if (!current || getPlotId(current as any) !== plotId) {
      return { ok: false, error: 'Plot not found or not current' };
    }

    // Clone baseline figure and normalize it
    const normalizedBaseline = normalizeFig(baselineFigure, current.plotlyJson);
    
    // Restore full plot state from baseline
    const restoredPlot: ClientPlot = {
      ...current,
      plotlyJson: normalizedBaseline as any,
    };

    // Update plots array
    const updatedPlots = state.plots.map(p => 
      getPlotId(p as any) === plotId ? restoredPlot : p
    );

    // Clear hydration guard dirty flags via markServerSaved pathway
    const guard = { ...(state.hydration?.guard || {}) } as HydrationState;
    const baselineHash = computeFigureHash(normalizedBaseline);
    const nextGuard: HydrationState = {
      ...guard,
      isDirty: false,
      dirtyEpoch: guard.epoch || 0, // Reset dirtyEpoch to current epoch so it doesn't block future hydrations
      baselineHash,
      baselineUpdatedAt: guard.baselineUpdatedAt || String(Date.now()),
    };

    // Reset store editHistory to single-entry history at index 0 (baseline plotSpec)
    // We reset both editHistory arrays: the legacy one in store and ensure consistency
    const baselinePlotSpec = normalizedBaseline as PlotSpecLike;
    const resetHistory = [baselinePlotSpec];
    const resetHistoryIndex = 0;

    set((s) => ({
      currentPlot: restoredPlot,
      plots: updatedPlots,
      editHistory: resetHistory,
      historyIndex: resetHistoryIndex,
      // Explicitly preserve baselineFigureById - baseline should remain available for future resets
      baselineFigureById: s.baselineFigureById || {},
      hydration: {
        ...(s.hydration || {}),
        guard: nextGuard,
      },
    }));

    // Mark as server saved to clear dirty flags
    try {
      get().markServerSaved({ figure: normalizedBaseline });
    } catch {}

    return { ok: true };
  },
}));

// Migration helper - map legacy fields to canonical theme schema
function migrateThemeToCanonical(fig: { layout?: any }) {
  if (!fig || !fig.layout) return;
  fig.layout.meta = fig.layout.meta || {};
  (fig.layout.meta as any).lumi = (fig.layout.meta as any).lumi || {};
  const lumi = (fig.layout.meta as any).lumi as any;
  lumi.theme = lumi.theme || {};
  const t = lumi.theme;
  // Legacy: theme.categorical -> swatches
  if (Array.isArray(t.categorical) && t.categorical.length > 0 && (!Array.isArray(t.swatches) || t.swatches.length === 0)) {
    t.swatches = t.categorical;
  }
  // Legacy: theme.continuous -> sequential.scale
  if (typeof t.continuous === 'string' && (!t.sequential || !t.sequential.scale)) {
    t.sequential = { ...(t.sequential || {}), scale: t.continuous };
  }
  // Seed if missing
  if (typeof t.seed !== 'number') {
    t.seed = Math.floor(Math.random() * 2 ** 31);
  }
  // Ensure mappings structure
  t.mappings = t.mappings || { by: null, domain: [], colors: {}, locked: [] };
  if (!Array.isArray(t.mappings.domain)) t.mappings.domain = [];
  if (!t.mappings.colors) t.mappings.colors = {};
  if (!Array.isArray(t.mappings.locked)) t.mappings.locked = [];
  if (!t.mappings.missing) (t.mappings as any).missing = '#9e9e9e';
  // Defaults for mode
  if (!t.mode) t.mode = 'categorical';
  // Defaults for coloraxis name
  if (!t.coloraxis) t.coloraxis = 'coloraxis';
  // Ensure notes object
  t.notes = t.notes || { color_adjustments: [], clamp: false, new_categories: [] };
}
